<template>
    <div>
        <div v-show="show" class="w-full flex justify-center">
            <div
                id="order-invoice"
                ref="orderTemplate"
                class="w-ticket bg-white text-black py-3 px-2"
            >
                <div class="flex flex-col items-center">
                    <img
                        :src="tenantLogoIconUrl"
                        crossorigin="anonymous"
                        width="80"
                        alt="Logo"
                        @error="onLogoLoadError"
                    />
                    <div
                        class="text-center flex flex-col items-center justify-center"
                    >
                        <div v-if="order.store" class="flex flex-col">
                            <span class="font-bold">
                                {{ order.store.name }}
                            </span>
                            <span class="text-sm">
                                {{ order.store.address }}
                            </span>
                        </div>

                        <small v-else class="font-bold">{{
                            tenantName || $publicUrl
                        }}</small>
                        <small>
                            {{ order.createdAt | date }}
                        </small>
                    </div>
                </div>

                <hr class="border-dashed border-t border-gray-300 my-2" />

                <div class="mb-3 pl-2">
                    <span
                        class="text-xs uppercase tracking-wide font-bold block mb-1"
                    >
                        Datos del pedido
                    </span>
                    <div
                        v-if="order.customer && order.customer.firstName"
                        class="flex items-end w-full"
                    >
                        <span class="font-bold">Cliente:&nbsp;</span>
                        <small class="capitalize">
                            {{
                                `${order.customer.firstName} ${order.customer.lastName}`
                                    | cut(30)
                            }}
                        </small>
                    </div>
                    <div
                        v-if="order.customer && order.customer.phone"
                        class="flex items-end w-full"
                    >
                        <span class="font-bold">Contacto:&nbsp;</span>
                        <small class="capitalize">
                            {{ order.customer.phone }}
                        </small>
                    </div>
                    <div
                        v-if="
                            order.paymentStatus ===
                            $data.$paymentStatuses.approved
                        "
                        class="flex items-end w-full"
                    >
                        <span class="font-bold"> Método de pago:&nbsp; </span>
                        <small>{{ paymentMethod }}</small>
                    </div>

                    <div
                        v-if="order.valuePaid < order.total"
                        class="flex items-end w-full"
                    >
                        <span class="font-bold"> Abono:&nbsp; </span>
                        <small>
                            {{ order.valuePaid | currency }}
                            {{ paymentMethod }} | Saldo
                            {{ (order.total - order.valuePaid) | currency }}
                        </small>
                    </div>
                    <div class="flex items-end w-full">
                        <span class="font-bold"> Fecha de entrega:&nbsp; </span>
                        <small>{{ order.deliveryAt | dateSimple }}</small>
                    </div>
                    <div
                        v-if="order.pickupMerchant && order.pickupMerchant.name"
                        class="flex items-center w-full"
                    >
                        <span class="font-bold"> Punto:&nbsp; </span>
                        <small>
                            {{ order.pickupMerchant.name }} |
                            {{ order.pickupMerchant.address }}
                        </small>
                    </div>
                    <div
                        v-if="order.location && order.location.address"
                        class="flex items-center w-full"
                    >
                        <span class="font-bold"> Domicilio:&nbsp; </span>
                        <small>
                            {{ order.location.address }}
                            {{ order.location.addressComplement }}
                        </small>
                    </div>
                </div>

                <hr class="border-dashed border-t border-gray-300 my-2" />

                <table
                    class="print border-b border-t border border-gray-300 border-collapse"
                >
                    <thead class="border-b-4 border-gray-600">
                        <tr>
                            <th scope="w-5/12" class="border-b border-gray-300">
                                <span class="font-bold text-sm">
                                    Producto
                                </span>
                            </th>
                            <th scope="w-1/12" class="border-b border-gray-300">
                                <span class="font-bold text-sm"> Uds. </span>
                            </th>
                            <th scope="w-1/4" class="border-b border-gray-300">
                                <span class="font-bold text-sm"> $Ud. </span>
                            </th>

                            <th scope="w-1/4" class="border-b border-gray-300">
                                <span class="font-bold text-sm"> Total </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in references" :key="index">
                            <td
                                class="w-5/12 pr-0 border-b border-gray-300 align-top"
                            >
                                <span class="text-sm break-words font-bold">
                                    {{ item.product.name }}
                                </span>
                                <br />
                                <small class="text-gray-500">
                                    #{{ item.id }}
                                    <span v-if="hasSize">
                                        | T {{ item.size || "N/A" }}
                                    </span>
                                    <span v-if="hasColor">
                                        | Color {{ item.color || "N/A" }}
                                    </span>
                                </small>
                            </td>
                            <th
                                scope="w-1/12 text-center"
                                class="border-b border-gray-300 align-top"
                            >
                                <span class="text-sm">
                                    {{ item.quantity }}
                                </span>
                            </th>
                            <td
                                class="w-1/4 text-center border-b border-gray-300 align-top"
                            >
                                <small>
                                    {{
                                        (item.priceOffer -
                                            item.appliedDiscountPerReference ||
                                            0) | currency(true, true)
                                    }}
                                </small>
                            </td>
                            <td
                                class="w-1/4 border-b border-gray-300 align-top"
                            >
                                <small>
                                    {{
                                        ((item.priceOffer -
                                            item.appliedDiscountPerReference ||
                                            0) *
                                            item.quantity)
                                            | currency(true, true)
                                    }}
                                </small>
                            </td>
                        </tr>
                        <tr>
                            <th
                                colspan="1"
                                scope="colgroup"
                                class="border-b border-gray-300"
                            >
                                <div
                                    class="h-full flex justify-center items-end py-2"
                                >
                                    <span class="font-bold text-lg m-0">
                                        Total
                                    </span>
                                </div>
                            </th>
                            <th
                                colspan="3"
                                scope="colgroup"
                                class="border-b border-gray-300"
                            >
                                <div
                                    class="h-full flex justify-center items-end py-2"
                                >
                                    <span class="h4 m-0">
                                        {{ order.total | currency(true, true) }}
                                    </span>
                                </div>
                            </th>
                        </tr>
                    </tbody>
                </table>

                <template v-if="order.customerNote">
                    <hr class="border-dashed border-t border-gray-300 my-2" />
                    <div
                        class="border border-dashed border-gray-400 rounded px-2 py-1 mt-2"
                    >
                        <span class="font-bold text-sm">Nota:&nbsp;</span>
                        <span class="text-sm break-words">
                            {{ order.customerNote }}
                        </span>
                    </div>
                </template>

                <hr class="border-dashed border-t border-gray-300 my-2" />

                <div class="mt-3 flex flex-col justify-center items-center">
                    <barcode-viewer :input="order.uuid" />
                    <small> #{{ order.id }} - {{ order.uuid }} </small>
                    <p class="text-center flex justify-center">¡GRACIAS!</p>
                    <br />
                    <small>Generado por POS tucatalogo.co</small>
                </div>
            </div>
        </div>
        <div v-if="showPrint" class="mt-4 w-full flex justify-center gap-2">
            <pdf-download-button @click="generatePdf" />
            <slot name="buttons"></slot>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { STATIC_ORDER_LOGO_PATH } from "@/common/constants";
import { paymentMethodsCodes } from "@/common/fields/orders-payment-methods";
import { paymentStatuses } from "@/common/fields/orders-payment-status-codes";
import { ordersStatuses } from "@/common/fields/orders-status-codes";
import { verifyImageLoads } from "@/common/lib/images";
import { downLoadPDF, elementToPDF, openPDF } from "@/common/lib/pdf";

import BarcodeViewer from "@/components/utils/BarcodeViewer.vue";
import PdfDownloadButton from "@/components/utils/PdfDownloadButton.vue";

export default {
    name: "OrderTicketPrinter",
    components: { BarcodeViewer, PdfDownloadButton },
    props: {
        show: {
            default: () => false,
            required: false,
            type: Boolean
        },
        order: {
            default: () => ({
                id: 0,
                uuid: "12123131",
                paymentMethod: "cash",
                paymentStatus: "approved",
                customerName: "",
                createdAt: "2022-07-15T02:39:03.833Z",
                total: 10,
                references: [],
                anonymousReferences: []
            }),
            required: false,
            type: Object
        },
        showPrint: {
            default: () => false,
            required: false,
            type: Boolean
        }
    },
    data: () => ({
        paymentMethod: paymentMethodsCodes.cash,
        references: [],
        tenantLogoIconUrl: STATIC_ORDER_LOGO_PATH,
        tenantName: "",
        $paymentStatuses: paymentStatuses,
        $ordersStatuses: ordersStatuses
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        hasSize() {
            return this.references.some((item) => !!item.size);
        },
        hasColor() {
            return this.references.some((item) => !!item.color);
        }
    },
    watch: {
        order(value) {
            this.setInput(value);
        }
    },
    async mounted() {
        this.tenantLogoIconUrl = await this.resolveTenantLogo("logoIconUrl");
        this.tenantName = await this.resolveTenantName();
        this.setInput(this.order);
    },
    methods: {
        ...mapActions("tenants", ["getCachedTenantBrandingSettings"]),
        async generatePdf() {
            try {
                const pdf = await this.$loading.wrap(() =>
                    elementToPDF(this.$refs.orderTemplate)
                );
                if (this.$isPhone) {
                    downLoadPDF(pdf, `orden_${this.order.id}`);
                } else {
                    openPDF(pdf, `orden_${this.order.id}`);
                }
            } catch (error) {
                this.showGenerationError(error);
            }
        },
        showGenerationError(error) {
            console.error("Error generando el PDF", error);
            this.$swal.fire({
                icon: "error",
                title: "No pudimos generar el PDF",
                text:
                    error?.message ||
                    (typeof error === "string" && error) ||
                    "Intenta de nuevo en unos segundos.",
                background: this.backgroundColor
            });
        },
        async resolveTenantLogo(field) {
            try {
                const branding = await this.getCachedTenantBrandingSettings();
                const remoteUrl = branding?.[field];
                if (remoteUrl && (await verifyImageLoads(remoteUrl))) {
                    return remoteUrl;
                }
                return STATIC_ORDER_LOGO_PATH;
            } catch {
                return STATIC_ORDER_LOGO_PATH;
            }
        },
        async resolveTenantName() {
            try {
                const branding = await this.getCachedTenantBrandingSettings();
                return branding?.name || "";
            } catch {
                return "";
            }
        },
        onLogoLoadError() {
            this.tenantLogoIconUrl = STATIC_ORDER_LOGO_PATH;
        },
        setInput(value) {
            if (value.paymentStatus === paymentStatuses.approved) {
                this.paymentMethod = paymentMethodsCodes[value.paymentMethod];
            }
            this.references = [
                ...value.references,
                ...value.anonymousReferences
            ].filter((item) => item.quantity);
        }
    }
};
</script>
